import { PERMISSIONS_BY_ROLES } from '@cp/core/permissions/roles';
import { NestedPermissionType, PermissionType, RootPermissionType } from '@cp/core/permissions/permissions';
import { OrganizationUserPermissionsT, RoleMappingT } from '@cp/core/permissions/types';
import { CustomerRole, CustomerRoleT } from '@/api/apis/customer';
import { RoleMapping } from '@/types/customer';

const resPermissionIfMatch = (
  hasMatch: boolean,
  permission: RootPermissionType | NestedPermissionType,
): PermissionType | null => {
  return hasMatch ? permission : null;
};

export const getPermissionsList = (roles: CustomerRoleT[]): PermissionType[] => {
  const hasMatchingRolePermission = (
    role: CustomerRoleT,
    permissionRoleMapping: RoleMapping<CustomerRoleT, CustomerRoleT>,
  ) => {
    const mappedRoles = permissionRoleMapping[role];
    return roles.includes(role) && mappedRoles.some((mappedRole: CustomerRoleT) => roles.includes(mappedRole));
  };
  const hasMatchingNestedPermission = (
    nestedPermissionKey: NestedPermissionType,
    permissionRoleMapping: RoleMapping<NestedPermissionType, CustomerRoleT>,
  ) => {
    const mappedRoles = permissionRoleMapping[nestedPermissionKey];
    return mappedRoles.some((mappedRole: CustomerRoleT) => roles.includes(mappedRole));
  };

  const permissions = Object.keys(PERMISSIONS_BY_ROLES).reduce<PermissionType[]>((acc, permission) => {
    const permissionKey = permission as RootPermissionType;

    const matchedPermissions = PERMISSIONS_BY_ROLES[permissionKey]
      .map((permissionRole: CustomerRoleT | OrganizationUserPermissionsT) => {
        if (typeof permissionRole === 'string') {
          return resPermissionIfMatch(roles.includes(permissionRole as CustomerRoleT), permissionKey);
        }

        const nestedPermissions = Object.keys(permissionRole as RoleMappingT)
          .map((key) => {
            if ((Object.values(CustomerRole) as string[]).includes(key)) {
              return resPermissionIfMatch(
                hasMatchingRolePermission(
                  key as CustomerRoleT,
                  permissionRole as RoleMapping<CustomerRoleT, CustomerRoleT>,
                ),
                permissionKey,
              );
            }

            return resPermissionIfMatch(
              hasMatchingNestedPermission(
                key as NestedPermissionType,
                permissionRole as RoleMapping<NestedPermissionType, CustomerRoleT>,
              ),
              key as NestedPermissionType,
            );
          })
          .filter((p): p is PermissionType => p !== null);

        return nestedPermissions.length > 0 ? nestedPermissions : null;
      })
      .flat()
      .filter((p): p is PermissionType => p !== null);

    if (matchedPermissions.length > 0) {
      return [...acc, ...matchedPermissions];
    }

    return acc;
  }, []);

  return Array.from(new Set(permissions));
};
